import { useCallback, useEffect, useState } from "react";

/**
 * Light/dark theme switch backed by `localStorage` and `prefers-color-scheme`.
 *
 * Toggles the `.dark` class on `<html>` because that's what shadcn's
 * `@custom-variant dark (&:is(.dark *))` (src/index.css) keys off, and
 * notifies every consumer of this hook so any component depending on
 * the current mode re-renders.
 *
 * Persistence semantics:
 * - `localStorage` is only written when the user explicitly toggles or
 *   calls setTheme. The initial render seeds state from localStorage if
 *   present, otherwise from the OS `prefers-color-scheme`, *without*
 *   writing back — so the OS preference keeps driving the UI until the
 *   user opts into an explicit choice.
 * - When no saved preference exists, a matchMedia listener follows OS
 *   changes live. Once the user toggles in-app, the saved choice wins
 *   and the listener becomes a no-op (until they reset by clearing
 *   `bf-theme` from storage).
 */
export type Theme = "light" | "dark";

const STORAGE_KEY = "bf-theme";
const ROOT = typeof document !== "undefined" ? document.documentElement : null;

// Every mounted useTheme() instance registers its setState here so a single
// toggle propagates to all of them. Setting state with the same value is a
// no-op for React, so the cross-instance fan-out doesn't loop.
const subscribers = new Set<(t: Theme) => void>();

function readSaved(): Theme | null {
  if (typeof window === "undefined") return null;
  const saved = window.localStorage.getItem(STORAGE_KEY);
  return saved === "light" || saved === "dark" ? saved : null;
}

function systemTheme(): Theme {
  if (typeof window === "undefined") return "light";
  return window.matchMedia("(prefers-color-scheme: dark)").matches
    ? "dark"
    : "light";
}

function readInitial(): Theme {
  return readSaved() ?? systemTheme();
}

function apply(theme: Theme) {
  if (!ROOT) return;
  ROOT.classList.toggle("dark", theme === "dark");
  subscribers.forEach((fn) => fn(theme));
}

export function useTheme() {
  const [theme, setThemeState] = useState<Theme>(readInitial);

  useEffect(() => {
    subscribers.add(setThemeState);
    return () => {
      subscribers.delete(setThemeState);
    };
  }, []);

  // Keep the `.dark` class in sync with state — but don't persist, since
  // persistence is now only triggered by an explicit user action below.
  useEffect(() => {
    if (ROOT) ROOT.classList.toggle("dark", theme === "dark");
  }, [theme]);

  // Live-follow the OS preference as long as no explicit choice is saved.
  // Once the user toggles in-app, `readSaved()` returns a value and this
  // listener short-circuits.
  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const handler = (event: MediaQueryListEvent) => {
      if (readSaved() === null) {
        apply(event.matches ? "dark" : "light");
      }
    };
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);

  const setTheme = useCallback((next: Theme) => {
    window.localStorage.setItem(STORAGE_KEY, next);
    apply(next);
  }, []);

  const toggleTheme = useCallback(() => {
    setTheme(theme === "dark" ? "light" : "dark");
  }, [theme, setTheme]);

  return { theme, setTheme, toggleTheme };
}
